import React, { useState, useEffect, useContext, useRef } from "react";
import { X, Check } from "lucide-react";
import axios from "axios";
import { useSampleRequest } from "../context/SampleRequestContext";
import { WishlistContext } from "../context/WishlistContext";
import { validateIndianMobile, PHONE_ERROR_MESSAGES } from "../utils/phone";
import { trackCompleteRegistration } from "../utils/metaPixel";

/**
 * Sample Request Modal
 * Full form for requesting marble samples
 */
const SampleRequestModal = () => {
  const { isOpen, closeModal, prefilledLots, source } = useSampleRequest();
  const { clearWishlist } = useContext(WishlistContext);

  // Form state
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [selectedLots, setSelectedLots] = useState([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Holds the URL (pathname + search + hash) that was active immediately
  // before we pushed /thank-you for Google Ads conversion tracking. Used to
  // restore the URL when the modal closes after success.
  const preSuccessUrlRef = useRef(null);

  // Load prefilled lots when modal opens
  useEffect(() => {
    if (isOpen && prefilledLots && prefilledLots.length > 0) {
      setSelectedLots([...prefilledLots]);
    }
  }, [isOpen, prefilledLots]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setPhone("");
        setName("");
        setCity("");
        setSelectedLots([]);
        setIsSuccess(false);
        setError("");
        setPhoneError("");
      }, 300);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleRemoveLot = (lotKey) => {
    setSelectedLots(
      selectedLots.filter(
        (lot) => (lot.lotId || `type-${lot.typeName}`) !== lotKey
      )
    );
  };

  // Wraps closeModal: if we pushed /thank-you for conversion tracking and
  // the URL is still on /thank-you, restore the previous URL silently before
  // closing. Prevents the URL from staying on /thank-you after dismissal.
  const handleClose = () => {
    try {
      if (
        preSuccessUrlRef.current &&
        typeof window !== "undefined" &&
        window.location.pathname === "/thank-you" &&
        window.history &&
        window.history.replaceState
      ) {
        window.history.replaceState({}, "", preSuccessUrlRef.current);
      }
    } catch (_) {
      // no-op
    }
    preSuccessUrlRef.current = null;
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPhoneError("");
    setError("");

    // Validate Indian mobile number (on submit only)
    const { valid, normalized, reason } = validateIndianMobile(phone);
    if (!valid) {
      setPhoneError(PHONE_ERROR_MESSAGES[reason] || PHONE_ERROR_MESSAGES.invalid);
      return;
    }

    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

      // Extract lot identifiers (lot IDs for specific lots, type-name for type-only entries)
      const lotIds = selectedLots.map(
        (lot) => lot.lotId || `type:${lot.typeName}`
      );

      await axios.post(`${backendUrl}/api/sample-requests`, {
        phone_number: normalized,
        name: name || null,
        city: city || null,
        lots_of_interest: lotIds,
        source: source || "unknown"
      });

      // Clear wishlist if source was wishlist drawer
      if (source === "wishlist_drawer") {
        clearWishlist();
      }

      // Google Ads conversion tracking - capture current URL, then silently
      // push /thank-you so the conversion tag fires. We restore the URL
      // when the modal closes (see handleClose). No visible navigation.
      try {
        if (typeof window !== "undefined" && window.history && window.history.pushState) {
          preSuccessUrlRef.current =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          window.history.pushState({}, "", "/thank-you");
        }
      } catch (_) {
        // no-op - tracking should never break submission flow
      }

      // Meta Pixel conversion tracking - fired on backend success
      trackCompleteRegistration();

      setIsSuccess(true);
    } catch (err) {
      console.error("Failed to submit sample request:", err);
      setError("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="sample-request-modal"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-[520px] bg-[#222] text-brand-cream animate-slide-up sm:animate-fade-in sm:mx-4 overflow-hidden"
        style={{
          maxHeight: "85vh",
          borderRadius: window.innerWidth < 640 ? "24px 24px 0 0" : "8px"
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          data-testid="modal-close-btn"
          aria-label="Close modal"
          className="absolute top-6 right-6 z-10 p-2 text-brand-gold/70 hover:text-brand-gold transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="overflow-y-auto max-h-[85vh] px-8 sm:px-12 py-10 sm:py-12">
          {isSuccess ? (
            // Success State
            <div data-testid="modal-success-state" className="text-center py-8">
              <div className="mb-6 flex justify-center">
                <div className="h-16 w-16 rounded-full bg-brand-gold/10 flex items-center justify-center">
                  <Check className="w-8 h-8 text-brand-gold" strokeWidth={2.5} />
                </div>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl font-light text-brand-cream mb-4">
                Your Samples Are On Their Way.
              </h2>

              <p className="font-sans text-base text-brand-cream/65 leading-relaxed max-w-md mx-auto mb-8">
                {name
                  ? `Thank you, ${name.split(" ")[0]}. Our team will WhatsApp you within 2 hours to confirm your sample visit. In the meantime, feel free to keep exploring our collection.`
                  : "Thank you. Our team will WhatsApp you within 2 hours to confirm your sample visit. In the meantime, feel free to keep exploring our collection."}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleClose}
                  data-testid="modal-continue-btn"
                  className="px-6 py-3 bg-brand-gold text-brand-charcoal font-sans text-sm font-medium uppercase tracking-[0.2em] transition-all hover:bg-brand-gold/90"
                  style={{ borderRadius: "5px" }}
                >
                  Continue Browsing
                </button>
                <a
                  href="https://wa.me/918796564619"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="modal-whatsapp-btn"
                  className="px-6 py-3 bg-transparent border border-brand-gold/40 text-brand-gold font-sans text-sm font-medium uppercase tracking-[0.2em] transition-all hover:bg-brand-gold/5"
                  style={{ borderRadius: "5px" }}
                >
                  WhatsApp Us Now
                </a>
              </div>
            </div>
          ) : (
            // Form State
            <>
              {/* Header */}
              <div className="mb-8">
                <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold mb-3">
                  Free Samples
                </p>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-brand-cream mb-3">
                  Bring Marble to Your Door.
                </h2>
                <p className="font-sans text-base text-brand-cream/65">
                  Tell us where to send our expert. No charge, no obligation.
                </p>
              </div>

              {/* Error Banner */}
              {error && (
                <div
                  data-testid="modal-error-banner"
                  className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 font-sans text-sm"
                  style={{ borderRadius: "5px" }}
                >
                  {error}
                </div>
              )}

              {/* Pre-filled Lots */}
              {selectedLots.length > 0 && (
                <div className="mb-8" data-testid="modal-lots-strip">
                  <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brand-cream/45 mb-3">
                    Lots You&apos;re Interested In
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLots.map((lot) => (
                      <div
                        key={lot.lotId || `type-${lot.typeName}`}
                        data-testid={`modal-lot-chip-${lot.lotId || lot.typeName}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-transparent border border-brand-cream/20 text-brand-cream font-sans text-sm"
                        style={{ borderRadius: "20px" }}
                      >
                        <span>
                          {lot.lotId
                            ? `Lot #${lot.lotId} - ${lot.typeName}`
                            : lot.typeName}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveLot(lot.lotId || `type-${lot.typeName}`)
                          }
                          data-testid={`modal-remove-lot-${lot.lotId || lot.typeName}`}
                          aria-label={`Remove ${lot.lotId ? `Lot ${lot.lotId}` : lot.typeName}`}
                          className="text-brand-gold/70 hover:text-brand-gold transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Phone Number */}
                <div>
                  <label
                    htmlFor="modal-phone"
                    className="block font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/65 mb-2"
                  >
                    WhatsApp Number
                  </label>
                  <input
                    id="modal-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setPhoneError("");
                    }}
                    placeholder="+91 98XXX XXXXX"
                    disabled={isSubmitting}
                    maxLength={15}
                    inputMode="tel"
                    autoComplete="tel"
                    data-testid="modal-phone-input"
                    className="w-full bg-transparent text-brand-cream font-sans text-base px-0 py-2.5 border-0 border-b border-brand-cream/30 focus:border-brand-gold/60 focus:outline-none transition-colors placeholder:text-brand-cream/30 disabled:opacity-50"
                  />
                  {phoneError ? (
                    <p
                      data-testid="modal-phone-error"
                      className="mt-2 font-sans text-xs text-red-400/80"
                    >
                      {phoneError}
                    </p>
                  ) : (
                    <p className="mt-2 font-sans text-xs text-brand-cream/40">
                      We&apos;ll WhatsApp you within 2 hours to confirm your visit.
                    </p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label
                    htmlFor="modal-name"
                    className="block font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/65 mb-2"
                  >
                    Name{" "}
                    <span className="text-brand-cream/40 normal-case tracking-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    id="modal-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    disabled={isSubmitting}
                    data-testid="modal-name-input"
                    className="w-full bg-transparent text-brand-cream font-sans text-base px-0 py-2.5 border-0 border-b border-brand-cream/30 focus:border-brand-gold/60 focus:outline-none transition-colors placeholder:text-brand-cream/30 disabled:opacity-50"
                  />
                </div>

                {/* City */}
                <div>
                  <label
                    htmlFor="modal-city"
                    className="block font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/65 mb-2"
                  >
                    City{" "}
                    <span className="text-brand-cream/40 normal-case tracking-normal">
                      (optional)
                    </span>
                  </label>
                  <select
                    id="modal-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    disabled={isSubmitting}
                    data-testid="modal-city-select"
                    className="w-full bg-transparent text-brand-cream font-sans text-base px-0 py-2.5 border-0 border-b border-brand-cream/30 focus:border-brand-gold/60 focus:outline-none transition-colors disabled:opacity-50 cursor-pointer"
                    style={{ WebkitAppearance: "none", MozAppearance: "none" }}
                  >
                    <option value="" className="bg-brand-charcoal">
                      Select your city
                    </option>
                    <option value="Bangalore" className="bg-brand-charcoal">
                      Bangalore
                    </option>
                    <option value="Mumbai" className="bg-brand-charcoal">
                      Mumbai
                    </option>
                    <option value="Delhi NCR" className="bg-brand-charcoal">
                      Delhi NCR
                    </option>
                    <option value="Hyderabad" className="bg-brand-charcoal">
                      Hyderabad
                    </option>
                    <option value="Chennai" className="bg-brand-charcoal">
                      Chennai
                    </option>
                    <option value="Pune" className="bg-brand-charcoal">
                      Pune
                    </option>
                    <option value="Other" className="bg-brand-charcoal">
                      Other
                    </option>
                  </select>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="modal-submit-btn"
                  className="w-full px-6 py-4 bg-brand-gold text-brand-charcoal font-sans text-sm font-medium uppercase tracking-[0.2em] transition-all hover:bg-brand-gold/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "5px" }}
                >
                  {isSubmitting ? "Submitting..." : "Request My Samples →"}
                </button>

                {/* WhatsApp Fallback */}
                <div className="text-center">
                  <a
                    href="https://wa.me/918796564619"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="modal-whatsapp-fallback"
                    className="font-sans text-sm text-brand-cream/55 hover:text-brand-gold transition-colors"
                  >
                    Or WhatsApp us directly →
                  </a>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SampleRequestModal;
